import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Check,
  Lock,
  Star,
  Brain,
  Rocket,
  Sparkles,
  Trophy,
  CalendarClock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useAuth } from "../context/AuthContext";
import { collection, doc, getDoc, getDocs, onSnapshot } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebase";
import { trackProductEvent } from "../lib/analytics";
import {
  formatTokenCount,
  getPlanDailyAIMentorTokenLimit,
} from "../constants/aiLimits";

/* ================= FIREBASE ================= */

const functions = getFunctions();
const createSubscription = httpsCallable(functions, "createSubscription");

/* ================= STYLES ================= */

const Page = styled.div`
  min-height: 100vh;
  padding: 120px 20px;
  background:
    radial-gradient(860px at 12% 0%, rgba(245, 158, 11, 0.12), transparent 58%),
    radial-gradient(900px at 88% 6%, rgba(56, 189, 248, 0.12), transparent 60%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Header = styled.div`
  max-width: 1100px;
  margin: auto;
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.1), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
`;

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 0.95fr);
  gap: 22px;
  align-items: start;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 16px 0 0;
  font-size: 42px;
  font-weight: 1000;
  line-height: 1.08;
  color: #f8fafc;
`;

const Sub = styled.p`
  margin-top: 12px;
  font-size: 18px;
  color: #94a3b8;
  line-height: 1.8;
  max-width: 760px;
`;

const ProofPanel = styled.div`
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.08)),
    rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(245, 158, 11, 0.18);
`;

const ProofLabel = styled.div`
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ProofTitle = styled.div`
  margin-top: 10px;
  color: #f8fafc;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 1000;
`;

const ProofList = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 10px;
`;

const ProofItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 16px;
  background: rgba(2, 6, 23, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 700;
`;

const Grid = styled.div`
  margin: 34px auto 0;
  max-width: 1100px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 32px;
`;

const TabsWrap = styled.div`
  max-width: 1100px;
  margin: 28px auto 0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const TabButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 999px;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(245, 158, 11, 0.34)" : "rgba(148, 163, 184, 0.14)"};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(245, 158, 11, 0.18), rgba(56, 189, 248, 0.12))"
      : "rgba(15, 23, 42, 0.52)"};
  color: ${({ $active }) => ($active ? "#f8fafc" : "#cbd5e1")};
  font-size: 13px;
  font-weight: 900;
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(245, 158, 11, 0.24);
  }
`;

const Card = styled.div`
  position: relative;
  padding: 34px;
  border-radius: 24px;
  height: 100%;
  background: ${({ highlight }) =>
    highlight
      ? "linear-gradient(135deg, rgba(245,158,11,0.18), rgba(56,189,248,0.1))"
      : "rgba(15,23,42,0.65)"};
  backdrop-filter: blur(18px);
  border: 1px solid
    ${({ highlight }) =>
      highlight ? "rgba(245, 158, 11, 0.22)" : "rgba(255, 255, 255, 0.08)"};
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
`;

const Badge = styled.div`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  background: linear-gradient(90deg, #f59e0b, #38bdf8);
  display: flex;
  gap: 6px;
  align-items: center;
`;

const Plan = styled.div`
  font-size: 13px;
  font-weight: 900;
  color: #94a3b8;
  letter-spacing: 0.08em;
`;

const Price = styled.div`
  margin-top: 12px;
  font-size: 38px;
  font-weight: 1000;
  color: #f8fafc;
`;

const Per = styled.span`
  font-size: 14px;
  opacity: 0.65;
`;

const List = styled.ul`
  margin-top: 26px;
  display: grid;
  gap: 14px;
  min-height: 288px;

  li {
    display: flex;
    gap: 10px;
    font-size: 14px;
    line-height: 1.5;
  }
`;

const CTA = styled.button`
  margin-top: 34px;
  width: 100%;
  padding: 14px;
  border-radius: 999px;
  border: none;
  font-weight: 900;
  font-size: 14px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  background: ${({ primary }) =>
    primary
      ? "linear-gradient(90deg, #f59e0b, #d97706)"
      : "rgba(148,163,184,0.18)"};
  color: ${({ disabled }) => (disabled ? "rgba(255,255,255,0.62)" : "#fff")};
  opacity: ${({ disabled }) => (disabled ? 0.72 : 1)};
`;

const Trust = styled.p`
  margin-top: 14px;
  font-size: 12px;
  color: #64748b;
  text-align: center;
`;

const CardSub = styled.p`
  margin-top: 12px;
  color: #94a3b8;
  font-size: 14px;
  line-height: 1.7;
`;

const LimitNote = styled.div`
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid rgba(56, 189, 248, 0.14);
  background: rgba(8, 15, 28, 0.42);
  color: #cbd5e1;
  font-size: 12px;
  line-height: 1.65;
`;

const MoreFeatures = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 800;
`;

const SkeletonBlock = styled.div`
  border-radius: ${({ $rounded = 16 }) => `${$rounded}px`};
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 20%,
    rgba(255, 255, 255, 0.12) 37%,
    rgba(255, 255, 255, 0.05) 63%
  );
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;

  @keyframes shimmer {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: -100% 0;
    }
  }
`;

const PricingLoader = styled.div`
  max-width: 1100px;
  margin: 34px auto 0;
  display: grid;
  gap: 32px;
`;

const LoaderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 32px;
`;

const LoaderCard = styled.div`
  padding: 34px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.65);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.28);
`;

const PLAN_FEATURE_LABELS = {
  roadmapAccess: "My Roadmap and roadmap execution",
  dashboard: "Progress dashboard and execution stats",
  community: "Community feed and posting access",
  savedNotes: "Saved notes and learning memory",
  aiRoadmapAssistant: "Daily AI Roadmap Assistant",
  aiMentors: "AI Mentors",
  calendarSync: "Google Calendar sync",
  emailReminders: "Daily email reminders",
  taskBoard: "Daily Task Board",
  roadmapCalendarView: "skilll.dev roadmap calendar view",
  sqlPractice: "SQL practice workspace",
};

const FREE_FEATURES = [
  "Full career roadmap",
  "Day-wise learning plan",
  "Mark days complete",
  "Basic progress dashboard",
  "Community access",
  "Saved notes",
  "Code Practice",
  "JSON Formatter",
  "Focus Without Phone",
];

const LOCKED_FREE_FEATURES = [
  "Daily AI Roadmap Assistant",
  "AI Mentors",
  "Daily email reminders",
  "Roadmap calendar view",
  "Google Calendar sync",
  "Daily Task Board",
];

const getDisplayFeatures = (plan) => {
  const flags = plan?.featureFlags || {};
  const enabledFlags = Object.entries(flags)
    .filter(([, value]) => value)
    .map(([key]) => PLAN_FEATURE_LABELS[key])
    .filter(Boolean);

  if (enabledFlags.length) {
    return enabledFlags;
  }

  if (Array.isArray(plan?.enabledFeatures) && plan.enabledFeatures.length) {
    const normalized = plan.enabledFeatures
      .map((key) => PLAN_FEATURE_LABELS[key] || null)
      .filter(Boolean);

    if (normalized.length) {
      return normalized;
    }
  }

  return Array.isArray(plan?.features) ? plan.features.filter(Boolean) : [];
};

const FEATURE_PREVIEW_COUNT = 6;
const PRICING_TABS = [
  {
    id: "learning-roadmap",
    label: "Learning Roadmap",
    icon: Brain,
    title: "Pricing for Learning Roadmaps",
    sub: "Start with roadmap clarity for free. Upgrade when you want stronger consistency, reminders, and roadmap-aware AI execution help.",
    proofTitle: "Structure for daily learning consistency",
    proofItems: [
      "Roadmap calendar view, reminders, and daily planning",
      "AI roadmap support based on your active plan",
      "Task board and execution visibility",
    ],
    freeCardTitle: "Start with roadmap clarity, day-wise structure, and a basic execution flow before paying for stronger systems.",
    freeFeatures: FREE_FEATURES,
    lockedFeatures: LOCKED_FREE_FEATURES,
    emptyMessage: "Learning roadmap plans will show here once they are added from the admin panel.",
  },
  {
    id: "product-development",
    label: "Product Development",
    icon: Rocket,
    title: "Pricing for AI Product Builder",
    sub: "Get the product development roadmap, then unlock the consistency system that keeps your self internship moving day by day.",
    proofTitle: "Execution support for real product building",
    proofItems: [
      "Daily development planning and execution systems",
      "AI daily development help for the current feature",
      "Reminder loops that keep builds moving",
    ],
    freeCardTitle: "Create the product roadmap for free. Upgrade when you want the consistency system around daily feature shipping.",
    freeFeatures: [
      "AI Product Builder roadmap",
      "Feature-by-feature phase and week planning",
      "Today’s feature and day task visibility",
      "Product build references inside roadmap days",
      "Saved notes",
      "Focus Without Phone",
    ],
    lockedFeatures: [
      "AI Daily Development Assistant",
      "Daily email reminders",
      "Roadmap calendar view",
      "Google Calendar sync",
      "Daily Task Board",
    ],
    emptyMessage: "Product development plans will show here once they are added from the admin panel.",
  },
  {
    id: "interview-prep",
    label: "Interview Prep",
    icon: Trophy,
    title: "Pricing for Interview Preparation",
    sub: "Prepare with a focused interview system instead of random practice. Unlock consistency, structure, and guided interview support when you need it.",
    proofTitle: "Consistency for interview preparation",
    proofItems: [
      "Structured interview prep execution support",
      "AI assistance tied to active preparation goals",
      "Reminder systems that keep interview prep active",
    ],
    freeCardTitle: "Start with interview clarity and self-practice. Upgrade when you want a stronger preparation system with reminders and AI guidance.",
    freeFeatures: [
      "Interview preparation roadmap access",
      "Basic progress visibility",
      "Community access",
      "Saved notes",
      "Code Practice",
      "SQL Practice",
    ],
    lockedFeatures: [
      "AI Mentors",
      "Daily email reminders",
      "Roadmap calendar view",
      "Google Calendar sync",
      "Daily Task Board",
    ],
    emptyMessage: "Interview prep plans will show here once they are added from the admin panel.",
  },
];

const normalizePlanProductLine = (plan = {}) => {
  const raw = String(
    plan.productLine ||
      plan.planType ||
      plan.category ||
      plan.segment ||
      plan.track ||
      ""
  )
    .trim()
    .toLowerCase();

  if (/product/.test(raw)) return "product-development";
  if (/interview|job|prep/.test(raw)) return "interview-prep";
  return "learning-roadmap";
};

/* ================= COMPONENT ================= */

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [currentPlan, setCurrentPlan] = useState("FREE");
  const [activePlanId, setActivePlanId] = useState("");
  const [activePlanTitle, setActivePlanTitle] = useState("");
  const [activePlanType, setActivePlanType] = useState("");
  const [subscriptionStatus, setSubscriptionStatus] = useState("");
  const [subscriptionItems, setSubscriptionItems] = useState([]);
  const [freeTrackStatus, setFreeTrackStatus] = useState({
    "learning-roadmap": false,
    "product-development": false,
    "interview-prep": false,
  });
  const [subscribingPlanId, setSubscribingPlanId] = useState(null);
  const [activeTab, setActiveTab] = useState("learning-roadmap");

  /* -------- Fetch plans -------- */
  useEffect(() => {
    async function loadPlans() {
      const snap = await getDocs(collection(db, "plans"));

      const activePlans = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => p.isActive)
        .sort((a, b) => a.price.monthly - b.price.monthly);
      setPlans(activePlans);
      setLoading(false);
    }

    loadPlans();
  }, []);

  /* -------- Listen for subscription success -------- */
  useEffect(() => {
    if (!user) return;

    const unsubUser = onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (snap.exists()) {
        const userData = snap.data();
        const nextPlan = userData.plan || "FREE";
        setCurrentPlan(nextPlan);
        setActivePlanId(String(userData.activePlanId || "").trim());
        setActivePlanTitle(String(userData.activePlanTitle || "").trim());
        setActivePlanType(normalizePlanProductLine(userData));

        if (nextPlan === "PRO") {
          setShowSuccess(true);
        }
      }
    });

    const unsubSubscription = onSnapshot(doc(db, "subscriptions", user.uid), (snap) => {
      if (!snap.exists()) {
        setSubscriptionStatus("");
        setSubscriptionItems([]);
        return;
      }

      const subscription = snap.data();
      setSubscriptionStatus(String(subscription.status || ""));
      const items = Array.isArray(subscription.subscriptions)
        ? subscription.subscriptions
        : Array.isArray(subscription.items)
        ? subscription.items
        : [subscription];
      setSubscriptionItems(items);
      setActivePlanId((prev) => prev || String(subscription.planId || "").trim());
      setActivePlanTitle((prev) => prev || String(subscription.plan || "").trim());
      setActivePlanType((prev) => prev || normalizePlanProductLine(subscription));
    });

    return () => {
      unsubUser();
      unsubSubscription();
    };
  }, [user]);

  useEffect(() => {
    let active = true;

    const loadRoadmapStatus = async () => {
      if (!user?.uid) {
        setFreeTrackStatus({
          "learning-roadmap": false,
          "product-development": false,
          "interview-prep": false,
        });
        return;
      }

      const [roadmapSnap, productRoadmapSnap] = await Promise.all([
        getDoc(doc(db, "roadmaps", user.uid)),
        getDoc(doc(db, "product_roadmaps", user.uid)),
      ]);

      if (active) {
        setFreeTrackStatus({
          "learning-roadmap": roadmapSnap.exists(),
          "product-development": productRoadmapSnap.exists(),
          "interview-prep": roadmapSnap.exists(),
        });
      }
    };

    loadRoadmapStatus();

    return () => {
      active = false;
    };
  }, [user]);

  /* -------- Razorpay flow -------- */
  const startSubscription = async (planId) => {
    try {
      setSubscribingPlanId(planId);
      const selectedPlan = plans.find((plan) => plan.planId === planId);

      void trackProductEvent(
        "subscription_checkout_started",
        {
          planId,
          planName: selectedPlan?.name || selectedPlan?.title || "PRO",
          monthlyPrice: selectedPlan?.price?.monthly || null,
        },
        { user }
      );

      const res = await createSubscription({
        planId,
        notes: { uid: user.uid },
      });

      const rzp = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY,
        subscription_id: res.data.id,
        name: "skilll.dev",
        description: "skilll.dev Subscription",
      });

      rzp.open();
    } catch (err) {
      console.error(err);
      alert("Payment failed. Try again.");
    } finally {
      setSubscribingPlanId(null);
    }
  };

  const activeTabMeta =
    PRICING_TABS.find((tab) => tab.id === activeTab) || PRICING_TABS[0];
  const freePlanActivated =
    !!freeTrackStatus[activeTab] && !subscriptionItems.some((item) => {
      const itemStatus = String(item?.status || "").toLowerCase();
      const itemType = normalizePlanProductLine(item || {});
      return (
        itemType === activeTab &&
        ["active", "authenticated", "pending", "cancel_requested"].includes(
          itemStatus
        )
      );
    });
  const filteredPlans = plans.filter(
    (plan) => normalizePlanProductLine(plan) === activeTab
  );
  const freeFeaturePreview = [
    ...activeTabMeta.freeFeatures,
    ...activeTabMeta.lockedFeatures,
  ];
  const freeVisibleFeatures = freeFeaturePreview.slice(0, FEATURE_PREVIEW_COUNT);
  const hasActivePaidSubscription =
    currentPlan === "PRO" &&
    ["active", "authenticated", "pending", "cancel_requested"].includes(
      String(subscriptionStatus || "active").toLowerCase()
    );

  const hasMatchingSubscribedPlan = (plan) => {
    const normalizedPlanType = normalizePlanProductLine(plan);
    const normalizedPlanId = String(plan.planId || "").trim();
    const normalizedPlanTitle = String(plan.title || "").trim().toUpperCase();

    const matchesPrimaryActiveFields =
      hasActivePaidSubscription &&
      ((activePlanId && activePlanId === normalizedPlanId) ||
        (!!activePlanTitle &&
          activePlanTitle.toUpperCase() === normalizedPlanTitle)) &&
      (!activePlanType || activePlanType === normalizedPlanType);

    if (matchesPrimaryActiveFields) return true;

    return subscriptionItems.some((item) => {
      const itemStatus = String(item?.status || "").toLowerCase();
      const itemType = normalizePlanProductLine(item || {});
      const itemPlanId = String(item?.planId || "").trim();
      const itemPlanTitle = String(
        item?.planTitle || item?.plan || ""
      ).trim().toUpperCase();

      const isActiveLike = ["active", "authenticated", "pending", "cancel_requested"].includes(
        itemStatus
      );

      return (
        isActiveLike &&
        itemType === normalizedPlanType &&
        ((itemPlanId && itemPlanId === normalizedPlanId) ||
          (!!itemPlanTitle && itemPlanTitle === normalizedPlanTitle))
      );
    });
  };

  const getFreePlanRoute = () => {
    if (activeTab === "product-development") {
      return freeTrackStatus["product-development"]
        ? "/my-product-roadmap"
        : "/create-product-roadmap";
    }

    if (activeTab === "interview-prep") {
      return freeTrackStatus["interview-prep"]
        ? "/my-roadmap"
        : "/career-clarity-home";
    }

    return freeTrackStatus["learning-roadmap"]
      ? "/my-roadmap"
      : "/career-clarity-home";
  };

  return (
    <Page>
      <Header>
        <HeaderGrid>
          <div>
            <Eyebrow>
              <Sparkles size={14} />
              {activeTabMeta.title}
            </Eyebrow>
            <Title>Simple pricing. Serious progress.</Title>
            <Sub>{activeTabMeta.sub}</Sub>
          </div>

          <ProofPanel>
            <ProofLabel>What paid unlocks</ProofLabel>
            <ProofTitle>{activeTabMeta.proofTitle}</ProofTitle>
            <ProofList>
              {activeTabMeta.proofItems.map((item) => (
                <ProofItem key={item}>
                  {item.includes("AI") ? (
                    <Brain size={16} />
                  ) : item.includes("reminder") || item.includes("Calendar") ? (
                    <CalendarClock size={16} />
                  ) : (
                    <Trophy size={16} />
                  )}
                  {item}
                </ProofItem>
              ))}
            </ProofList>
          </ProofPanel>
        </HeaderGrid>
      </Header>

      <TabsWrap>
        {PRICING_TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <TabButton
              key={tab.id}
              type="button"
              $active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={15} />
              {tab.label}
            </TabButton>
          );
        })}
      </TabsWrap>

      {loading ? (
        <PricingLoader>
          <LoaderGrid>
            {[1, 2, 3].map((card) => (
              <LoaderCard key={card}>
                <SkeletonBlock style={{ width: 84, height: 12 }} />
                <SkeletonBlock style={{ width: 180, height: 46, marginTop: 16 }} />
                <SkeletonBlock style={{ width: "100%", height: 18, marginTop: 16 }} />
                <SkeletonBlock style={{ width: "84%", height: 18, marginTop: 10 }} />

                <div style={{ marginTop: 28, display: "grid", gap: 14, minHeight: 288 }}>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <SkeletonBlock style={{ width: 16, height: 16, borderRadius: 999 }} />
                      <SkeletonBlock style={{ width: `${88 - index * 6}%`, height: 14 }} />
                    </div>
                  ))}
                </div>

                <SkeletonBlock style={{ width: "100%", height: 66, marginTop: 18, borderRadius: 16 }} />
                <SkeletonBlock style={{ width: "100%", height: 52, marginTop: 26, borderRadius: 999 }} />
              </LoaderCard>
            ))}
          </LoaderGrid>
        </PricingLoader>
      ) : (
      <Grid>
        {/* -------- FREE PLAN -------- */}
        <Card>
          <Plan>FREE</Plan>
          <Price>₹0</Price>
          <CardSub>
            {activeTabMeta.freeCardTitle}
          </CardSub>

          <List>
            {freeVisibleFeatures.map((feature) => {
              const isLocked = activeTabMeta.lockedFeatures.includes(feature);

              return (
              <li key={feature}>
                {isLocked ? <Lock size={16} /> : <Check size={16} />} {feature}
              </li>
            );
            })}
          </List>

          <CTA
            disabled={freePlanActivated}
            onClick={() =>
              !freePlanActivated && navigate(getFreePlanRoute())
            }
          >
            {freePlanActivated ? "Free Activated" : "Start Free"}
          </CTA>

          <Trust>
            {freePlanActivated
              ? "Your free roadmap access is already active"
              : "No credit card required"}
          </Trust>
        </Card>

        {/* -------- PAID PLANS (DYNAMIC) -------- */}
        {filteredPlans.map((plan) => {
          const tokenLimitLabel = formatTokenCount(
            getPlanDailyAIMentorTokenLimit(plan)
          );
          const displayFeatures = getDisplayFeatures(plan);
          const visibleFeatures = displayFeatures.slice(0, FEATURE_PREVIEW_COUNT);
          const remainingFeatureCount = Math.max(
            displayFeatures.length - FEATURE_PREVIEW_COUNT,
            0
          );
          const isCurrentSubscribedPlan = hasMatchingSubscribedPlan(plan);

          return (
          <Card key={plan.id} highlight={plan.isPopular}>
            {plan.isPopular && (
              <Badge>
                <Star size={14} /> Most Popular
              </Badge>
            )}

            <Plan>{plan.title.toUpperCase()}</Plan>

            <Price>
              ₹{plan.price.monthly} <Per>/ month</Per>
            </Price>
            <CardSub>
              {plan.isPopular
                ? "Best for users who want stronger consistency support in this track."
                : "Designed for users who want more than static planning and need systems that keep momentum alive."}
            </CardSub>

            <List>
              {visibleFeatures.map((feature) => (
                <li key={feature}>
                  <Check size={16} /> {feature}
                </li>
              ))}
            </List>

            {remainingFeatureCount > 0 && (
              <MoreFeatures>+ {remainingFeatureCount} more features</MoreFeatures>
            )}

            <LimitNote>
              Includes up to {tokenLimitLabel} AI mentor tokens per day for
              roadmap help, unblock support, and focused next-step guidance.
            </LimitNote>

            <CTA
              primary
              disabled={subscribingPlanId === plan.planId || isCurrentSubscribedPlan}
              onClick={() =>
                !isCurrentSubscribedPlan &&
                subscribingPlanId !== plan.planId &&
                startSubscription(plan.planId)
              }
            >
              {isCurrentSubscribedPlan
                ? "Subscribed"
                : subscribingPlanId === plan.planId
                ? "Opening Razorpay..."
                : plan.ctaText ||
                (plan.isPopular ? (
                  <>
                    <Brain size={16} /> Learn with Mentor
                  </>
                ) : (
                  <>
                    <Rocket size={16} /> Subscribe
                  </>
                ))}
            </CTA>
          </Card>
        );
        })}
        {!filteredPlans.length && (
          <Card>
            <Plan>{activeTabMeta.label.toUpperCase()}</Plan>
            <Price>Coming Soon</Price>
            <CardSub>{activeTabMeta.emptyMessage}</CardSub>
            <List>
              {activeTabMeta.lockedFeatures
                .slice(0, FEATURE_PREVIEW_COUNT)
                .map((feature) => (
                  <li key={feature}>
                    <Lock size={16} /> {feature}
                  </li>
                ))}
            </List>
            <CTA onClick={() => navigate("/feedback")}>I want this next</CTA>
            <Trust>Tell us which track you want us to prioritize first.</Trust>
          </Card>
        )}
      </Grid>
      )}

      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ marginTop: 40, textAlign: "center" }}
          >
            🎉 Subscription active. Let’s build consistency.
          </motion.div>
        )}
      </AnimatePresence>
    </Page>
  );
}
